import { Box, BoxProps, Icon, TypeIconName, useResponsive } from '@bitrise/bitkit';
import { NAV_WIDTH_OPEN } from '@/constants';

type NavButtonProps = {
  href?: string;
  children?: React.ReactNode;
  title?: string;
  iconName?: TypeIconName;
  iconActiveName?: TypeIconName;
  match?: 'prefix' | 'full';
} & BoxProps;

const matchHref = (type: 'prefix' | 'full', href?: string) => {
  if (!href) return false;
  const { pathname } = window.location;
  if (type === 'prefix') return pathname.startsWith(href);
  return pathname === href;
};

const NavButton = ({
  href,
  children,
  title,
  iconName,
  iconActiveName,
  match = 'prefix',
  ...props
}: NavButtonProps): JSX.Element => {
  const { pathname } = window.location;
  const { isMobile } = useResponsive();
  let isActive = matchHref(match, href);

  const iconColor = isMobile ? 'purple.10' : 'neutral.100';

  if (!isActive && href === '/dashboard') {
    isActive = pathname.startsWith('/build/') || pathname.startsWith('/app/');
  }

  return (
    <Box
      as={href ? 'a' : undefined}
      href={href}
      display="flex"
      alignItems="center"
      textColor={['purple.10', 'neutral.100']}
      fontWeight={[isActive ? 600 : 400, 600]}
      width={['auto', NAV_WIDTH_OPEN]}
      height={[48, 56]}
      textAlign="center"
      gap="4"
      position="relative"
      backgroundColor={isActive ? ['purple.95', 'rgba(255, 255, 255, .2)'] : 'transparent'}
      opacity={isActive ? 1 : 0.8}
      paddingX="24"
      _hover={{
        backgroundColor: ['neutral.93', 'rgba(255, 255, 255, .1)'],
        opacity: 1,
      }}
      _active={{
        backgroundColor: ['neutral.93', 'rgba(255, 255, 255, .2)'],
        opacity: 1,
      }}
      _after={
        isActive
          ? {
              background: ['purple.50', 'rgba(255, 255, 255, .8)'],
              width: '4',
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
            }
          : {}
      }
      {...props}
    >
      {iconName && (
        <Icon
          name={isActive && iconActiveName ? iconActiveName : iconName}
          textColor={iconColor}
          marginRight={[12, 24]}
        />
      )}
      {title}
      {children}
    </Box>
  );
};

export default NavButton;
